// src/components/FormQuestionRenderer.jsx
import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import AnnotationCanvas from "./AnnotationCanvas";

export default function FormQuestionRenderer({ q, value, onChange, readOnly }) {
  const t = (q?.type || "").toLowerCase().trim();

  switch (t) {
    case "signature":
      return (
        <SignatureInput
          question={q}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    case "media":
      return (
        <MediaInput
          question={q}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    case "annotation":
      return (
        <AnnotationInput
          question={q}
          value={value}
          onChange={onChange}
          readOnly={readOnly}
        />
      );

    default:
      return (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={{ width: "85%", padding: 6 }}
        />
      );
  }
}

/* --------------------
/* --------------------
   MEDIA INPUT COMPONENT
   -------------------- */
function MediaInput({ question, value = [], onChange, readOnly }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const allowMultiple = question.allowMultiple ?? true;
  const allowComments = question.allowComments ?? true;
  const maxFiles =
    typeof question.maxFiles === "number" && question.maxFiles > 0
      ? question.maxFiles
      : null;

  const currentCount = Array.isArray(value) ? value.length : 0;
  const atLimit = maxFiles != null && currentCount >= maxFiles;
  const canAdd = !readOnly && !atLimit;

  async function handleSelectFiles(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    let selected = allowMultiple ? files : files.slice(0, 1);

    if (maxFiles != null) {
      const remaining = Math.max(0, maxFiles - currentCount);
      selected = selected.slice(0, remaining);
    }

    if (!selected.length) {
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];

      for (const f of selected) {
        const form = new FormData();
        form.append("file", f);

        // 🔹 Use shared API helper so we always hit the correct backend
        const json = await api.upload("/upload", form);
        if (!json?.url) {
          throw new Error("Upload failed: no URL returned from server");
        }

        uploaded.push({
          url: json.url,
          name: f.name,
          comment: "",
        });
      }

      onChange([...(value || []), ...uploaded]);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || "Media upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function removeFile(idx) {
    if (readOnly) return;
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  }

  function updateComment(idx, comment) {
    if (!allowComments || readOnly) return;
    const next = [...(value || [])];
    next[idx] = { ...next[idx], comment };
    onChange(next);
  }

  return (
    <div style={{ marginTop: 6 }}>
      {canAdd && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            background: "#1976d2",
            color: "#fff",
            padding: "6px 16px",
            borderRadius: 6,
            marginBottom: 10,
            border: "none",
            fontSize: 14,
          }}
        >
          {uploading ? "Uploading..." : "Add Media"}
        </button>
      )}

      {maxFiles != null && (
        <div
          style={{
            fontSize: 11,
            color: "#6b7280",
            marginBottom: 6,
          }}
        >
          {currentCount} / {maxFiles} files
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple={allowMultiple}
        style={{ display: "none" }}
        onChange={handleSelectFiles}
      />

      {(value || []).map((file, idx) => (
        <div
          key={idx}
          className="inspection-media-row"
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            marginTop: 8,
          }}
        >
          {/* IMAGE */}
          <a
            href={file.url}
            target="_blank"
            rel="noreferrer"
            style={{
              display: "block",
              flexShrink: 0,
              maxWidth: 350, // optional upper limit
            }}
          >
            <div
              style={{
                width: "100%",
                borderRadius: 10,
                background: "#e5e7eb",
              }}
            >
              <img
                className="inspection-media-image"
                src={file.url}
                alt={file.name || ""}
                style={{
                  width: "100%", // scales within the <a> width
                  height: "auto", // natural height from the image
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
          </a>

          {/* COMMENT + ACTIONS — STRETCHES ACROSS REST OF ROW */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeFile(idx)}
                style={{
                  marginBottom: 6,
                  background: "#ef4444",
                  color: "#fff",
                  border: "none",
                  padding: "4px 10px",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              >
                Remove
              </button>
            )}

            {allowComments && !readOnly && (
              <textarea
                placeholder="Comment"
                value={file.comment || ""}
                onChange={(e) => updateComment(idx, e.target.value)}
                style={{
                  width: "100%",
                  fontSize: 12,
                  padding: 6,
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                }}
              />
            )}

            {readOnly && file.comment && (
              <div
                style={{
                  fontSize: 14,
                  color: "#374151",
                  whiteSpace: "pre-wrap",
                }}
              >
                💬 {file.comment}
              </div>
            )}
          </div>
        </div>
      ))}

      {!value?.length && (
        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            color: "#9ca3af",
            fontStyle: "italic",
          }}
        >
          No media attached yet.
        </div>
      )}
    </div>
  );
}

/* -----------------------
   ANNOTATION INPUT COMPONENT
   ----------------------- */
function AnnotationInput({ value, onChange, readOnly }) {
  const fileInputRef = useRef(null);
  const [isPadOpen, setIsPadOpen] = useState(false);

  const normalized =
    value && typeof value === "object"
      ? value
      : { baseImageUrl: null, annotatedImage: null, comment: "" };

  const baseImageUrl = normalized.baseImageUrl || null;
  const annotatedImage = normalized.annotatedImage || null;
  const comment = normalized.comment || "";

  function handleCommentChange(e) {
    onChange({
      ...normalized,
      comment: e.target.value,
    });
  }

  function handleUploadClick() {
    if (readOnly) return;
    fileInputRef.current?.click();
  }

  async function handleFileSelect(e) {
    const file = (e.target.files || [])[0];
    if (!file) return;

    try {
      const form = new FormData();
      form.append("file", file);
      const json = await api.upload("/upload", form);
      if (!json?.url) throw new Error("Upload failed: no URL returned");

      onChange({
        ...normalized,
        baseImageUrl: json.url,
        annotatedImage: null,
      });
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.message || "Annotation image upload failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleOpenPad() {
    if (readOnly) return;
    setIsPadOpen(true);
  }

  function handleOverlayChange(dataUrl) {
    onChange({
      ...normalized,
      baseImageUrl,
      annotatedImage: dataUrl,
      comment,
    });
  }

  // Delete the current image + annotation
  function handleRemoveImage() {
    if (readOnly) return;
    onChange({
      ...normalized,
      baseImageUrl: null,
      annotatedImage: null,
    });
  }

  // Download the annotated image (or base image if no annotation yet)
  function handleDownloadImage() {
    const url = annotatedImage || baseImageUrl;
    if (!url) return;

    const link = document.createElement("a");
    link.href = url;
    link.download = "annotation.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  if (readOnly) {
    return (
      <div style={{ marginTop: 4 }}>
        {annotatedImage || baseImageUrl ? (
          <div
            style={{
              borderRadius: 10,
              border: "1px solid #d1d5db",
              padding: 8,
              background: "#f9fafb",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {baseImageUrl && (
                <img
                  src={baseImageUrl}
                  alt="Annotation base"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              )}
              {annotatedImage && (
                <img
                  src={annotatedImage}
                  alt="Annotation overlay"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "auto", // 🔹 match base image scaling
                    pointerEvents: "none",
                  }}
                />
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "#9ca3af",
              fontStyle: "italic",
            }}
          >
            No annotation added
          </div>
        )}
        {comment && (
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              color: "#374151",
              whiteSpace: "pre-wrap",
            }}
          >
            💬 {comment}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      {/* INLINE PREVIEW */}
      {(annotatedImage || baseImageUrl) && (
        <div
          style={{
            borderRadius: 10,
            border: "1px solid #d1d5db",
            padding: 8,
            background: "#f9fafb",
            marginBottom: 8,
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {baseImageUrl && (
              <img
                src={baseImageUrl}
                alt="Annotation base"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            )}
            {annotatedImage && (
              <img
                src={annotatedImage}
                alt="Annotation overlay"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "auto", // 🔹 match base image scaling
                  pointerEvents: "none",
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* ACTIONS ROW */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        {/* LEFT SIDE: Upload + Annotate */}
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={handleUploadClick}
            style={{
              background: "#e5e7eb",
              color: "#111827",
              padding: "6px 12px",
              borderRadius: 8,
              border: "none",
              fontSize: 13,
            }}
          >
            📎 {baseImageUrl ? "Add image" : "Add image"}
          </button>

          <button
            type="button"
            onClick={handleOpenPad}
            style={{
              background: "#4b5563",
              color: "#fff",
              padding: "6px 14px",
              borderRadius: 8,
              border: "none",
              fontSize: 13,
            }}
          >
            ✏️ Annotate
          </button>
        </div>

        {/* RIGHT SIDE: Save + Delete */}
        {(baseImageUrl || annotatedImage) && (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={handleDownloadImage}
              style={{
                background: "#e5e7eb",
                color: "#111827",
                padding: "6px 10px",
                borderRadius: 8,
                border: "none",
                fontSize: 13,
              }}
            >
              ⬇️
            </button>

            <button
              type="button"
              onClick={handleRemoveImage}
              style={{
                background: "#fee2e2",
                color: "#b91c1c",
                padding: "6px 10px",
                borderRadius: 8,
                border: "none",
                fontSize: 13,
              }}
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />

      {/* COMMENT */}
      <textarea
        placeholder="Annotation comment..."
        value={comment}
        onChange={handleCommentChange}
        style={{
          width: "100%",
          minHeight: 50,
          padding: 6,
          borderRadius: 6,
          border: "1px solid #d1d5db",
          fontSize: 13,
        }}
      />

      {/* MODAL CANVAS */}
      <AnnotationCanvas
        isOpen={isPadOpen}
        baseImageUrl={baseImageUrl}
        overlayDataUrl={annotatedImage}
        onChangeOverlay={handleOverlayChange}
        onClose={() => setIsPadOpen(false)}
      />
    </div>
  );
}

/* ------------------------
   SIGNATURE INPUT COMPONENT
   ------------------------ */
function SignatureInput({ value, onChange, readOnly }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const normalized = typeof value === "object" && value !== null ? value : {};
  const hasSignature = !!normalized.dataUrl;

  function getPos(e) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    let clientX;
    let clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }

  function handleStart(e) {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    if (!pos) return;

    drawingRef.current = true;
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111827";
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function handleMove(e) {
    if (!drawingRef.current || readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    if (!pos) return;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }

  function handleEnd(e) {
    if (!drawingRef.current || readOnly) return;
    e.preventDefault();
    drawingRef.current = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    onChange({
      ...normalized,
      dataUrl,
      signedAt: Date.now(),
    });
  }

  function handleClear() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  }

  if (readOnly) {
    if (hasSignature) {
      return (
        <div style={{ marginTop: 4 }}>
          <div
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              background: "#f9fafb",
              padding: 8,
              display: "inline-block",
            }}
          >
            <img
              src={normalized.dataUrl}
              alt="Signature"
              style={{ display: "block", maxWidth: 260, height: "auto" }}
            />
          </div>
          {normalized.signedAt && (
            <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>
              Signed: {new Date(normalized.signedAt).toLocaleString()}
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        style={{
          marginTop: 4,
          fontSize: 12,
          color: "#9ca3af",
          fontStyle: "italic",
        }}
      >
        No signature captured
      </div>
    );
  }

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          border: "1px dashed #9ca3af",
          borderRadius: 10,
          padding: 8,
          background: "#f9fafb",
        }}
      >
        <canvas
          ref={canvasRef}
          width={480}
          height={160}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            background: "#ffffff",
            borderRadius: 8,
          }}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
        />
        <div
          style={{
            marginTop: 6,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 11,
            color: "#6b7280",
          }}
        >
          <span>Sign inside the box</span>
          <button
            type="button"
            onClick={handleClear}
            style={{
              border: "none",
              background: "transparent",
              color: "#ef4444",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>
      {hasSignature && (
        <div style={{ marginTop: 4, fontSize: 11, color: "#6b7280" }}>
          Signature captured
        </div>
      )}
    </div>
  );
}
